import { useRef, useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useDisableBootstrap } from "./hooks/useDisableBootstrap";
import { translationsSpanish } from "./i18n/translationsSpanish";
import { OpenInYouTubeItem } from "./components/OpenInYouTube";
import { PlayButton } from "./components/PlayButton";
import { UnmuteButton } from "./components/UnmuteButton";
import "@vidstack/react/player/styles/default/theme.css";
import "@vidstack/react/player/styles/default/layouts/video.css";
import { MediaPlayer, MediaProvider, Poster, isYouTubeProvider, useMediaState } from "@vidstack/react";
import { defaultLayoutIcons, DefaultVideoLayout } from "@vidstack/react/player/layouts/default";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import "./playerV2.css";

/* 
  http://localhost:3000/?config=watch&video_provider=youtube&video_id=fJ9rUzIMcZQ&autoplay=false&poster=https://i1.ytimg.com/vi/fJ9rUzIMcZQ/hqdefault.jpg
  http://localhost:3000/?config=watch&video_provider=youtube&video_id=fJ9rUzIMcZQ
  http://localhost:3000/?config=watch&video_provider=youtube&video_id=9hGQ3P8ZcLQ&lang=en
  !ERROR http://localhost:3000/?config=watch&video_provider=vimeo&video_id=325654952
  http://localhost:3000/?config=watch&video_provider=vimeo&video_id=640499893
  http://localhost:3000/?config=watch&video_provider=vimeo&video_id=1159478734
  http://localhost:3000/?config=watch&video_provider=vimeo&video_id=1159510590&autoplay=false
  http://localhost:3000/?config=watch&video_provider=vimeo&video_id=640499893&autoplay=true&fullscreen_on_play=true
  http://localhost:3000/?config=watch&video_provider=vimeo&video_id=640499893&autoplay=false&fullscreen_on_play=true

  - video_provider: "youtube" | "vimeo"
  - video_id: ID del video en la plataforma correspondiente
  - autoplay: true | false (default: true)
  - poster: URL para imagen
  - lang: "en" | "es" (default: "es")
  - show_yt_button: true | false (default: false)
*/
function PlayerV2() {
  useDisableBootstrap();

  let player = useRef(null);
  let provider = useRef(null);

  let videoSrc;
  // const auth = useAuth();
  const [muted, setMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showOpenInYoutube, setShowOpenInYoutube] = useState(false);
  const hasAttemptedFullscreen = useRef(false);
  const hasSentCompletion = useRef(false);

  const [searchParams] = useSearchParams();
  const videoId = searchParams.get("v");
  const videoConfig = JSON.parse(localStorage.getItem(`video_${videoId}`));
  const title = useMediaState("title", player);
  const isFullscreen = useMediaState("fullscreen", player);

  const tryFallbackFullscreen = useCallback(() => {
    const video = player.current?.querySelector?.('video');
    if (video?.webkitEnterFullscreen) {
      video.webkitEnterFullscreen();
    }
  }, []);

  const handleCustomFullscreen = useCallback(() => {
    player.current?.enterFullscreen().catch(() => {
      tryFallbackFullscreen();
    });
  }, [tryFallbackFullscreen]);

  // Reset completion flag when video changes
  useEffect(() => {
    hasSentCompletion.current = false;
    hasAttemptedFullscreen.current = false;
  }, [videoId]);

  if (!videoConfig) {
    return (
      <div className="player-container">
        <p className="no-video-message">No hay un video disponible para reproducir.</p>
      </div>
    );
  }

  const isAutoplay = videoConfig.autoplay !== "false";

  if (videoConfig.provider === "youtube") {
    videoSrc = `youtube/${videoId}`;
  } else if (videoConfig.provider === "vimeo") {
    videoSrc = `vimeo/${videoId}`;
  } else {
    videoSrc = null;
  }

  if (!videoSrc) {
    return (
      <div className="player-container">
        <p className="error-message">Proveedor de video no válido.</p>
      </div>
    );
  }


  function onTimeUpdate({ currentTime }) {
    const duration = player.current?.duration;
    if (!duration || hasSentCompletion.current) return;
    const percent = (currentTime / duration) * 100;
    if (percent >= 80) {
      hasSentCompletion.current = true;
      window.parent.postMessage(
        { type: "tipconsola:videoProgress", percent, completed: true },
        "https://abastible.tipddy.cl"
      );
    }
  }

  function onProviderChange(provider) {
    if (!provider) return;

    if (isYouTubeProvider(provider)) {
      setShowOpenInYoutube(true);
      provider.language = videoConfig.language;
    } else {
      setShowOpenInYoutube(false);
    }
  }

  function onVolumeChange(volume) {
    if (!player?.current) return;

    if (volume === 0 && !player.current.muted) {
      player.current.muted = true;
    }
  }

  const handleUnmute = () => {
    setMuted(false);
    if (player.current) {
      player.current.muted = false;
      player.current.volume = 1;
    }
  };

  function handlePlay() {
    setIsPlaying(true);
  }

/*   const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  // iOS YouTube: iframe directo con fullscreen nativo
  if (isIOS && videoConfig.provider === 'youtube' && videoConfig.fullscreen_on_play === 'true') {
    return (
      <div className="player-container">
        <iframe
          title="youtube"
          src={`https://www.youtube.com/embed/${videoId}?autoplay=0&controls=1&fullscreen=1`}
          style={{ width: '100%', height: '100%', border: 'none' }}
          allow="autoplay; fullscreen; encrypted-media"
          allowFullScreen
        />
      </div>
    );
  } */

  return (
    <>
      {videoConfig.video_id && (
        <div className="player-container">
          {title && (
            <div
              id="video-title"
              data-testid="video-title"
              className="sr-only"
              style={{ position: "absolute", opacity: 0, pointerEvents: "none" }}
            >
              {title}
            </div>
          )}
          <MediaPlayer
            ref={player}
            className="player"
            src={videoSrc}
            autoPlay={isAutoplay}
            muted={isAutoplay && muted}
            onPlay={handlePlay}
            onPause={() => setIsPlaying(false)}
            onProviderChange={onProviderChange}
            onMediaVolumeChangeRequest={onVolumeChange}
            onTimeUpdate={onTimeUpdate}
            playsInline
            crossOrigin
          >
            <MediaProvider ref={provider}>
              {videoConfig.poster && <Poster className="vds-poster" src={videoConfig.poster} alt={player?.current?.title} />}
            </MediaProvider>
            <DefaultVideoLayout
              icons={defaultLayoutIcons}
              translations={translationsSpanish}
              slots={{
                fullscreenButton: (
                  <button
                    className="vds-button vds-fullscreen-button"
                    onClick={handleCustomFullscreen}
                  >
                    {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                  </button>
                ),
                settingsMenuItemsEnd: showOpenInYoutube && <OpenInYouTubeItem videoId={videoId} />,
              }}
            />
          </MediaPlayer>

          {isAutoplay && muted && UnmuteButton(muted, handleUnmute)}

          {/* Overlay play para móvil con fullscreen_on_play: activa provider + reproduce en 1 tap */}
          {videoConfig.fullscreen_on_play === 'true' && !isAutoplay && !isPlaying && (
            <div
              className="play-button-container paused"
              onClick={() => {
                player.current?.startLoading();
                setMuted(false);
                if (player.current) {
                  player.current.muted = false;
                  player.current.volume = 1;
                }
                player.current?.play();
                if (videoConfig.fullscreen_on_play === 'true' && !hasAttemptedFullscreen.current) {
                  hasAttemptedFullscreen.current = true;
                  player.current?.enterFullscreen().catch(() => {
                    tryFallbackFullscreen();
                  });
                }
              }}
            >
              <div className="play-button">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" className="play-icon">
                  <path d="M320-273v-414q0-17 12-28.5t28-11.5q5 0 10.5 1.5T381-721l326 207q9 6 13.5 15t4.5 19q0 10-4.5 19T707-446L381-239q-5 3-10.5 4.5T360-233q-16 0-28-11.5T320-273Z" />
                </svg>
              </div>
            </div>
          )}
          {/* {!isAutoplay && !isPlaying && PlayButton(player)} */}
        </div>
      )}
    </>
  );
}

export default PlayerV2;
